import Echo from 'laravel-echo'
import Pusher from 'pusher-js'
import { api } from '@/lib/api/client'

interface ChannelAuthorization {
  auth: string
}

/** The body of `POST /broadcasting/auth`, in the API's `data` envelope. */
interface ChannelAuthorizationResponse {
  data: ChannelAuthorization
}

type AuthorizationCallback = (
  error: Error | null,
  authorization: ChannelAuthorization | null,
) => void

/** Authorizes through `api`, so the request carries the Bearer token and a 401 signs the user out. */
async function authorizeChannel(
  channelName: string,
  socketId: string,
  callback: AuthorizationCallback,
): Promise<void> {
  try {
    const { data } = await api.post<ChannelAuthorizationResponse>('broadcasting/auth', {
      socket_id: socketId,
      channel_name: channelName,
    })
    callback(null, data.data)
  } catch (error) {
    callback(error instanceof Error ? error : new Error(String(error)), null)
  }
}

/** A connection to the Reverb server, or `null` when this environment has no key configured. */
export function createEcho(): Echo<'reverb'> | null {
  const key = import.meta.env.VITE_REVERB_APP_KEY
  if (!key) return null

  return new Echo({
    broadcaster: 'reverb',
    Pusher,
    key,
    wsHost: import.meta.env.VITE_REVERB_HOST,
    wsPort: import.meta.env.VITE_REVERB_PORT,
    wssPort: import.meta.env.VITE_REVERB_PORT,
    forceTLS: import.meta.env.VITE_REVERB_SCHEME === 'https',
    enabledTransports: ['ws', 'wss'],
    authorizer: (channel: { name: string }) => ({
      authorize: (socketId: string, callback: AuthorizationCallback) =>
        authorizeChannel(channel.name, socketId, callback),
    }),
  })
}
